import { createContext, useState, useContext, useEffect } from "react";
import { Global } from "@emotion/react";
import useTouchFeedback from "../hooks/useTouchFeedback";

const AnimationContext = createContext();

export function AnimationProvider({ children }) {
  useTouchFeedback();
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const stored = localStorage.getItem("animationsEnabled");
    if (stored !== null) return stored === "true";
    // Respect OS/browser reduced-motion preference as default
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    localStorage.setItem("animationsEnabled", animationsEnabled);
  }, [animationsEnabled]);

  // Which kiosk swipe-instruction act(s) to animate — lets a kiosk whose
  // hardware only has one reader (e.g. no card slot) show just the
  // relevant animation instead of cycling through both.
  const [qrScanEnabled, setQrScanEnabledState] = useState(() => {
    const stored = localStorage.getItem("kioskQrScanEnabled");
    return stored === null ? false : stored === "true";
  });
  const [cardScanEnabled, setCardScanEnabledState] = useState(() => {
    const stored = localStorage.getItem("kioskCardScanEnabled");
    return stored === null ? true : stored === "true";
  });

  useEffect(() => {
    localStorage.setItem("kioskQrScanEnabled", qrScanEnabled);
  }, [qrScanEnabled]);

  useEffect(() => {
    localStorage.setItem("kioskCardScanEnabled", cardScanEnabled);
  }, [cardScanEnabled]);

  // At least one of the two must stay on — turning one on always re-enables
  // it, but turning one off is refused if it's the only one left enabled.
  function setQrScanEnabled(value) {
    setQrScanEnabledState((prev) => {
      if (!value && !cardScanEnabled) return prev;
      return value;
    });
  }
  function setCardScanEnabled(value) {
    setCardScanEnabledState((prev) => {
      if (!value && !qrScanEnabled) return prev;
      return value;
    });
  }

  return (
    <AnimationContext.Provider
      value={{
        animationsEnabled,
        setAnimationsEnabled,
        qrScanEnabled,
        setQrScanEnabled,
        cardScanEnabled,
        setCardScanEnabled,
      }}
    >
      {/* inject global styles when animations are disabled */}
      {!animationsEnabled && (
        <Global
          styles={{
            "*, *::before, *::after": {
              animation: "none !important",
              transition: "none !important",
              // transform: "none !important",
            },
            "*": {
              scrollBehavior: "auto !important",
            },
          }}
        />
      )}
      {children}
    </AnimationContext.Provider>
  );
}

export function useAnimationToggle() {
  return useContext(AnimationContext);
}
